// src/pages/OnlineTests/TestPage.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Timer from "../../components/Tests/Timer";
import SidebarPalette from "../../components/Tests/SidebarPalette";
import QuestionPanel from "../../components/Tests/QuestionPanel";
import { postRequest, fetchTestDetailsQuestions, fetchTestDetailsOngoing } from "../../services/api";
import TestSummaryModal from "../OnlineTests/TestSummaryModal";
import "./TestPage.css";
import Logo from "../../acade_logo1.png";
import { formatSlugUpperCase } from "../../utils/slugify";

/* ─── Types ─────────────────────────────────────────────── */
interface Question {
  id:          string;
  question:    string;
  optionA:     string;
  optionB:     string;
  optionC:     string;
  optionD:     string;
  sectionName: string; // always set — used in every API call
}

interface SectionData {
  section_name:   string;
  subject:        string;
  question_marks: string | number;
  negative_marks: string | number;
  questions:      Question[];
}

interface TestDetails {
  subject?:        string;
  timing?:         string;
  test_name?:      string;
  question_marks?: string;
  negative_marks?: string;
  test_type?:      "single" | "multi";
  sections?:       any[];
}

interface QStatus {
  answered:          boolean;
  marked_for_review: boolean;
  visited:           boolean;
}

/* ─── Helpers ────────────────────────────────────────────── */
const toSecs = (t = "00:00:00") => {
  const [h, m, s] = t.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

const getUser = () => JSON.parse(localStorage.getItem("user") || "null");

const doSessionRefresh = async (userId: string, testId: string | undefined) => {
  const res = await fetchTestDetailsOngoing(userId, testId);
  const { answers, time_left } = res.data;
  return {
    opts:     Object.fromEntries(answers.map((a: any) => [a.question_id, a.selected_option])),
    status:   Object.fromEntries(answers.map((a: any) => [a.question_id, { answered: a.answered, marked_for_review: a.marked_for_review, visited: a.visited }])),
    timers:   Object.fromEntries(answers.map((a: any) => [a.question_id, a.time_taken || 0])),
    timeLeft: time_left,
  };
};

/* ════════════════════════════════════════════════════════ */
const TestPage = () => {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const navigate      = useNavigate();

  /* ── State ── */
  const [allQuestions,    setAllQuestions]    = useState<Question[]>([]);
  const [sections,        setSections]        = useState<SectionData[]>([]);
  const [isMulti,         setIsMulti]         = useState(false);
  const [currentQIdx,     setCurrentQIdx]     = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<{ [k: string]: string }>({});
  const [questionStatus,  setQuestionStatus]  = useState<{ [k: string]: QStatus }>({});
  const [questionTimers,  setQuestionTimers]  = useState<{ [k: string]: number }>({});
  const [testDetails,     setTestDetails]     = useState<TestDetails | null>(null);
  const [timeLeft,        setTimeLeft]        = useState(0);
  const [isPaused,        setIsPaused]        = useState(false);
  const [username,        setUsername]        = useState("");
  const [showModal,       setShowModal]       = useState(false);

  const timeRef             = useRef(0);
  const intervalRef         = useRef<NodeJS.Timeout | null>(null);
  const questionIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /* ── Derived ── */
  const currentQuestion = allQuestions[currentQIdx];

  /*
   * Which section is the current question in?
   * Compute from sections' cumulative question counts.
   */
  const currentSectionIdx = (() => {
    let acc = 0;
    for (let i = 0; i < sections.length; i++) {
      acc += sections[i].questions.length;
      if (currentQIdx < acc) return i;
    }
    return 0;
  })();

  const activeSection = sections[currentSectionIdx];

  /* Per-section marks (falls back to test-level for single) */
  const qMarks   = activeSection?.question_marks  ?? testDetails?.question_marks  ?? "1";
  const negMarks = activeSection?.negative_marks  ?? testDetails?.negative_marks  ?? "0";

  /* Question number within current section */
//   const sectionOffset = (() => {
//     let acc = 0;
//     for (let i = 0; i < currentSectionIdx; i++) acc += sections[i].questions.length;
//     return acc;
//   })();
  //const localQNum = currentQIdx - sectionOffset + 1;

  /* ════════════════════════════════════════════
     FETCH TEST + QUESTIONS
  ════════════════════════════════════════════ */
  useEffect(() => {
    if (!slug || !id) return;
    let mounted = true;

    (async () => {
      try {
        const resp  = await fetchTestDetailsQuestions(slug, id);
        const test  = resp.data.test  as TestDetails;
        const rawQs = resp.data.questions as any[];

        if (!mounted) return;
        setTestDetails(test);
        setTimeLeft(toSecs(test.timing));
        timeRef.current = toSecs(test.timing);

        const user = getUser();
        if (!user) { alert("Please login first."); return; }
        setUsername(user.firstname);

        /* ── Build sections + flat questions ── */
        const multi = test.test_type === "multi"
          && Array.isArray(test.sections)
          && test.sections.length > 0;
        setIsMulti(multi);

        let builtSections: SectionData[] = [];
        let flat: Question[]             = [];

        if (multi) {
          let offset = 0;
          builtSections = (test.sections || []).map((sec: any) => {
            /*
             * Use actual questions array length if no_of_questions is missing.
             * Number() guards against DB storing it as a string.
             */
            const count = Number(sec.no_of_questions) || sec.questions?.length || 0;
            const slice = rawQs.slice(offset, offset + count);
            const secQs: Question[] = slice.map((q: any) => ({
              id:          q._id,
              question:    q.question,
              optionA:     q.option_a,
              optionB:     q.option_b,
              optionC:     q.option_c,
              optionD:     q.option_d,
              sectionName: sec.section_name, // FIX #4
            }));
            flat   = [...flat, ...secQs];
            offset += count;
            return {
              section_name:   sec.section_name,
              subject:        sec.subject        || "",
              question_marks: sec.question_marks || 1,
              negative_marks: sec.negative_marks || 0,
              questions:      secQs,
            };
          });
        } else {
          const secQs: Question[] = rawQs.map((q: any) => ({
            id:          q._id,
            question:    q.question,
            optionA:     q.option_a,
            optionB:     q.option_b,
            optionC:     q.option_c,
            optionD:     q.option_d,
            sectionName: test.test_name || "General",
          }));
          builtSections = [{
            section_name:   test.test_name || "General",
            subject:        test.subject   || "General",
            question_marks: test.question_marks || "1",
            negative_marks: test.negative_marks || "0",
            questions:      secQs,
          }];
          flat = secQs;
        }

        setSections(builtSections);
        setAllQuestions(flat);

        /* ── Load / start session ── */
        try {
          const s = await doSessionRefresh(user.id, id);
          if (!mounted) return;
          setSelectedOptions(s.opts);
          setQuestionStatus(s.status);
          setQuestionTimers(s.timers);
          setTimeLeft(s.timeLeft);
          timeRef.current = s.timeLeft;
        } catch (e: any) {
          if (e?.response?.status === 404) {
            await postRequest("/online-test/start", { user_id: user.id, test_id: id, firstname: user.firstname, lastname: user.lastname, email: user.email });
          }
        }
      } catch (e) {
        console.error("TestPage error:", e);
      }
    })();

    return () => { mounted = false; };
  }, [slug, id]);

  /* ── Sync time every 10s ── */
  useEffect(() => {
    if (!allQuestions.length) return;
    const user = getUser();
    if (!user) return;
    const iv = setInterval(() => {
      postRequest("/online-test/update-time-left", { user_id: user.id, test_id: id, time_left: timeRef.current });
    }, 10_000);
    return () => clearInterval(iv);
  }, [id, allQuestions]);

  /* ── Countdown ── */
  useEffect(() => {
    if (!allQuestions.length) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!isPaused && timeRef.current > 0) {
        timeRef.current -= 1;
        setTimeLeft(timeRef.current);
        if (timeRef.current === 0) { clearInterval(intervalRef.current!); handleFinalSubmit(); }
      }
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [allQuestions, isPaused]); // eslint-disable-line

  /* ── Per-question timer ── */
  useEffect(() => {
    if (!allQuestions.length || isPaused || !currentQuestion) return;
    if (questionIntervalRef.current) clearInterval(questionIntervalRef.current);
    questionIntervalRef.current = setInterval(() => {
      setQuestionTimers(prev => ({
        ...prev,
        [currentQuestion.id]: (prev[currentQuestion.id] ?? 0) + 1,
      }));
    }, 1000);
    return () => { if (questionIntervalRef.current) clearInterval(questionIntervalRef.current); };
  }, [allQuestions, currentQIdx, isPaused]); // eslint-disable-line

  /* ════════════════════════════════════════════
     SUBMIT HELPER — section_name always included
  ════════════════════════════════════════════ */
  const buildPayload = (q: Question, extra: Record<string, any> = {}) => {
    const user = getUser();
    const sel  = selectedOptions[q.id];
    return {
      user_id:           user?.id,
      test_id:           id,
      question_id:       q.id,
      section_name:      q.sectionName,   // FIX #4 — always present
      selected_option:   sel ?? null,
      time_taken:        questionTimers[q.id] || 0,
      answered:          !!sel,
      marked_for_review: false,
      visited:           true,
      ...extra,
    };
  };

  const doRefresh = async () => {
    const user = getUser();
    if (!user) return;
    const s = await doSessionRefresh(user.id, id);
    setSelectedOptions(s.opts);
    setQuestionStatus(s.status);
  };

  /* ── Auto-fullscreen ── */
  useEffect(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  }, []);

  /* ════════════════════════════════════════════
     HANDLERS
  ════════════════════════════════════════════ */
  const handleOptionChange = (qId: string, option: string) =>
    setSelectedOptions(prev => ({ ...prev, [qId]: option }));

  const handleNext = async () => {
    if (!currentQuestion) return;
    await postRequest("/online-test/submit-answer", buildPayload(currentQuestion));
    await doRefresh();
    if (currentQIdx < allQuestions.length - 1) setCurrentQIdx(p => p + 1);
  };

  const handlePrev = async () => {
    if (!currentQuestion) return;
    await postRequest("/online-test/submit-answer", buildPayload(currentQuestion));
    await doRefresh();
    if (currentQIdx > 0) setCurrentQIdx(p => p - 1);
  };

  const handleMarkForReview = async () => {
    if (!currentQuestion) return;
    await postRequest("/online-test/submit-answer", buildPayload(currentQuestion, { marked_for_review: true }));
    await doRefresh();
    if (currentQIdx < allQuestions.length - 1) setCurrentQIdx(p => p + 1);
  };

  /*
   * Clears the selected option for the CURRENT question — both locally
   * (instant UI feedback) and on the backend (same submit-answer endpoint,
   * but with selected_option explicitly null + answered:false), so a
   * previously saved answer is actually cleared in MongoDB too, not just
   * hidden in the UI. Unlike Save/Next, this does not advance the question.
   */
  const handleClearResponse = async () => {
    if (!currentQuestion) return;
    const qId  = currentQuestion.id;
    const user = getUser();

    setSelectedOptions(prev => {
      const next = { ...prev };
      delete next[qId];
      return next;
    });
    setQuestionStatus(prev => ({
      ...prev,
      [qId]: {
        ...(prev[qId] || { marked_for_review: false, visited: true }),
        answered: false,
      },
    }));

    await postRequest("/online-test/submit-answer", {
      user_id:           user?.id,
      test_id:           id,
      question_id:       qId,
      section_name:      currentQuestion.sectionName,
      selected_option:   null,
      time_taken:        questionTimers[qId] || 0,
      answered:          false,
      marked_for_review: questionStatus[qId]?.marked_for_review || false,
      visited:           true,
    });
    await doRefresh();
  };

  /* FIX #2: globalIdx comes from SidebarPalette's sectionOffset + localIdx */
  const handlePaletteClick = async (globalIdx: number) => {
    if (!currentQuestion) return;
    await postRequest("/online-test/submit-answer", buildPayload(currentQuestion));
    const targetQ = allQuestions[globalIdx];
    if (targetQ) {
      // mark target as visited
      setQuestionStatus(prev => ({
        ...prev,
        [targetQ.id]: {
          ...(prev[targetQ.id] || { answered: false, marked_for_review: false }),
          visited: true,
        },
      }));
      await postRequest("/online-test/submit-answer", buildPayload(targetQ, { visited: true }));
    }
    await doRefresh();
    setCurrentQIdx(globalIdx);
  };

  /* Jump to first question of a section when tab clicked */
  const handleSectionTabClick = async (sIdx: number) => {
    if (!currentQuestion) return;
    await postRequest("/online-test/submit-answer", buildPayload(currentQuestion));
    await doRefresh();
    // Calculate offset for section sIdx from actual question counts
    let offset = 0;
    for (let i = 0; i < sIdx; i++) offset += sections[i].questions.length;
    setCurrentQIdx(offset);
  };

  const togglePause = () => {
    setIsPaused(p => !p);
    const user = getUser();
    if (user) postRequest("/online-test/update-time-left", { user_id: user.id, test_id: id, time_left: timeRef.current });
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  };

  const handleFinalSubmit = useCallback(async () => {
    const user = getUser();
    if (!user) return;
    try {
      await postRequest("/online-test/submit", { user_id: user.id, test_id: id, time_left: timeRef.current });
      navigate(`/online-tests/test-result/${slug}/${id}`);
    } catch (e) { console.error("Submit error:", e); }
  }, [id, navigate, slug]);

  /* ── Stats ── */
  const totalQ   = allQuestions.length;
  const ansCount = Object.values(questionStatus).filter(q => q.answered).length;
  const visCount = Object.values(questionStatus).filter(q => q.visited).length;
  let mfr = 0, mfrAns = 0, mfrNotAns = 0;
  Object.values(questionStatus).forEach(q => {
    if (q.marked_for_review) { mfr++; q.answered ? mfrAns++ : mfrNotAns++; }
  });

  /*
   * "Not Answered" (red palette cells) must mean *visited but left blank* —
   * not "total minus answered", which was wrongly folding in every question
   * the student hadn't even reached yet (that's "Not Visited", tracked
   * separately). Also excludes marked-for-review questions since those have
   * their own bucket below.
   */
  const notAnsweredCount = Object.values(questionStatus).filter(
    q => q.visited && !q.answered && !q.marked_for_review
  ).length;

  /*
   * Plain "Answered" (green) must exclude ones already counted under
   * "Marked & Answered" (purple-tick), otherwise those get double-counted
   * and the buckets stop summing to totalQuestions.
   */
  const plainAnsweredCount = ansCount - mfrAns;

  const summary = {
    sectionName: slug, totalQuestions: totalQ,
    answered: plainAnsweredCount, notAnswered: notAnsweredCount,
    markedForReview: mfr, markForReviewAndAnswered: mfrAns,
    markForReviewNotAnswered: mfrNotAns, notVisited: totalQ - visCount,
  };

  /* ── Loading ── */
  if (!allQuestions.length) return (
    <div className="test-page-container">
      <div className="test-header">
        <div className="test-header-left"><img src={Logo} alt="logo" /></div>
        <div className="test-header-right"><span>Loading…</span></div>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div className="test-page-container">

      {/* TOP HEADER */}
      <div className="test-header">
        <div className="test-header-left">
          <Link to="/"><img src={Logo} alt="logo" /></Link>
          <span className="test-name-label"> — {formatSlugUpperCase(slug || "")}</span>
        </div>
        <div className="test-header-right">
          <Timer timeLeft={timeLeft} onTimeUp={() => setShowModal(true)} isPaused={isPaused} />
          <button className="header-btn" onClick={togglePause}>{isPaused ? "Resume" : "Pause"}</button>
          <button className="header-btn" onClick={toggleFullScreen}>Fullscreen</button>
        </div>
      </div>

      {/* SECTION TABS — multi only, replaces the old "SECTION: X" row */}
      {isMulti && sections.length > 1 && (
        <div className="section-tabs-bar">
          <strong>SECTIONS:</strong> {sections.map((sec, sIdx) => (
            <button
              key={sIdx}
              className={`section-tab${sIdx === currentSectionIdx ? " active" : ""}`}
              onClick={() => handleSectionTabClick(sIdx)}
            >
              {sec.section_name}
              <span className="tab-count">{sec.questions.length}Q</span>
            </button>
          ))}
        </div>
      )}

      <div className="test-body">

        {/* ══ LEFT PANEL ══ */}
        <div className="left-panel">
          <div className="scrollable-content">

            {/* FIX #3: for multi, NO duplicate "SECTION: X" row below tabs */}
            {/* {!isMulti && ( */}
              <div className="sections-header hide-mobile">
                TEST NAME: {testDetails?.test_name || ""}
              </div>
            {/* )} */}

            <div className="question-header d-flex justify-content-between align-items-center">
              <div>Question No {currentQIdx + 1}</div>
              <div>
                <span className="tp-badge tp-badge-marks">+{qMarks}</span>
                <span className="tp-badge tp-badge-neg">-{negMarks}</span>
                <span className="tp-badge tp-badge-time">Time Taken: {questionTimers[currentQuestion?.id] || 0}s</span>
              </div>
            </div>

            <div className="question-area">
              {currentQuestion && (
                <QuestionPanel
                  question={currentQuestion}
                  selectedOption={selectedOptions[currentQuestion.id]}
                  onOptionChange={handleOptionChange}
                />
              )}
            </div>

            {/* Mobile sidebar */}
            <div className="mobile-sidebar">
              <div className="user-section">
                <div className="avatar">👤</div>
                <span>{username}</span>
              </div>
              <hr />
              <Legend s={summary} />
              <hr />
              <SidebarPalette
                sections={sections}
                questionStatus={questionStatus}
                onPaletteClick={handlePaletteClick}
                currentQIdx={currentQIdx}
                isMulti={isMulti}
              />
            </div>
          </div>

          {/* Bottom controls */}
          <div className="bottom-controls">
            <button onClick={handlePrev} disabled={currentQIdx === 0}>Previous</button>
            <button onClick={handleMarkForReview}>Mark for Review &amp; Next</button>
            <button
              className="clear-btn"
              onClick={handleClearResponse}
              disabled={!currentQuestion || !selectedOptions[currentQuestion.id]}
            >
              Clear Response
            </button>
            <button onClick={handleNext}>
              {currentQIdx === allQuestions.length - 1 ? "Save" : "Save & Next"}
            </button>
            <button className="submit-btn" onClick={() => setShowModal(true)}>Submit Test</button>
          </div>
        </div>

        {/* ══ DESKTOP SIDEBAR ══ */}
        <div className="sidebar">
          <div className="user-section">
            <div className="avatar">👤</div>
            <span>{username}</span>
          </div>
          <hr />
          <Legend s={summary} />
          <hr />
          {/* FIX #1: sp-wrap inside sidebar scrolls independently */}
          <div className="sidebar-palette-wrap">
            <SidebarPalette
              sections={sections}
              questionStatus={questionStatus}
              onPaletteClick={handlePaletteClick}
              currentQIdx={currentQIdx}
              isMulti={isMulti}
            />
          </div>
        </div>
      </div>

      <TestSummaryModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleFinalSubmit}
        summary={summary}
      />
    </div>
  );
};

/* ── Legend ── */
const Legend: React.FC<{ s: any }> = ({ s }) => (
  <div className="status-legend">
    <div className="status-item"><div className="status-circle answered">{s.answered}</div> Answered</div>
    <div className="status-item"><div className="status-circle notanswered">{s.notAnswered}</div> Not Answered</div>
    <div className="status-item"><div className="status-circle review">{s.markForReviewNotAnswered}</div> Marked for Review</div>
    <div className="status-item">
      <div className="status-circle review-answered with-tick">{s.markForReviewAndAnswered}<span className="tick-icon">✓</span></div>
      Marked &amp; Answered
    </div>
    <div className="status-item"><div className="status-circle not-visited">{s.notVisited}</div> Not Visited</div>
  </div>
);

export default TestPage;